import { RolodexEntryType } from 'types'
import { z } from 'zod'

const TagsSchema = z.record(z.string(), z.string().nullable())
const OptionalTimestamps = z.object({
  updated_at: z.coerce.date().nullish(),
  created_at: z.coerce.date().nullish(),
  last_updated_by: z.string().nullish(),
})

/**
 * Sanitizes sensor IDs in an expression by replacing hyphens and dots with underscores
 */
const sanitizeExpression = (
  expression: string,
  dependencyIds: string[]
): string => {
  let sanitized = expression

  // Replace each dependency ID in the expression with its sanitized version
  dependencyIds.forEach((id) => {
    const sanitizedId = id.replaceAll('-', '_').replaceAll('.', '_')
    // Use word boundaries to avoid partial matches
    const regex = new RegExp(id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
    sanitized = sanitized.replace(regex, sanitizedId)
  })

  return sanitized
}

export const TimeSeriesDerivedValueDataSchema = z
  .object({
    name: z.string(),
    expression: z.string(),
    unit: z.string().nullish(),
    description: z.string().nullish(),
    dependency_ids: z.array(z.string()),
  })
  .transform((schema) => ({
    name: schema.name,
    expression: sanitizeExpression(schema.expression, schema.dependency_ids),
    unit: schema.unit,
    description: schema.description,
    dependencyIds: schema.dependency_ids,
  }))

export const TimeSeriesDerivedValueEntryJSONSchema = z
  .object({
    id: z.string(),
    data: TimeSeriesDerivedValueDataSchema,
    tags: TagsSchema,
    ...OptionalTimestamps.shape,
  })
  .transform((schema) => ({
    id: schema.id,
    type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
    data: schema.data,
    tags: schema.tags,
    ...(schema.updated_at && { updatedAt: schema.updated_at }),
    ...(schema.created_at && { createdAt: schema.created_at }),
    ...(schema.last_updated_by && {
      lastUpdatedBy: schema.last_updated_by,
    }),
  }))

export const TimeSeriesDerivedValueEntrySchema = z.object({
  entry: TimeSeriesDerivedValueEntryJSONSchema,
})

export type TimeSeriesDerivedValueData = z.infer<
  typeof TimeSeriesDerivedValueDataSchema
>
export type TimeSeriesDerivedValueEntryJSON = z.infer<
  typeof TimeSeriesDerivedValueEntryJSONSchema
>
export type TimeSeriesDerivedValueEntry = z.infer<
  typeof TimeSeriesDerivedValueEntrySchema
>

export const NEW_DERIVED_VALUE_ENTRY: TimeSeriesDerivedValueEntryJSON = {
  id: '',
  type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
  data: {
    name: '',
    expression: '',
    unit: '',
    description: '',
    dependencyIds: [],
  },
  tags: {},
}
